import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CommonConstant, CommonUtility, PalletService, ToteService } from '../utility';
import { useDebouncedEffect } from './debounce';
import type { PalletLabelDataType } from 'types/warehoue/label';

export const PalletListWarehouseHook = (
  warehouseId,
  status = 'ready_to_pack',
  shouldFetch = true,
  search,
) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
    sort: {
      sortBy: 'status',
      sortOrder: CommonConstant.sortDirection.ascending,
    },
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...filter,
        ...filter.sort,
        sort: null,
        warehouseId,
      } as any;

      if (status) {
        params.status = status;
      }

      if (params.search) {
        params.search = params.search.trim();
      }

      const res = await PalletService.get(params);
      setData(res?.data || []);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterChanged('search', search);
  }, [search]);

  useDebouncedEffect(
    () => {
      if (warehouseId && shouldFetch) fetchData();
    },
    [filter, warehouseId, refreshKey, shouldFetch],
    300,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const refresh = () => {
    setRefreshKey(refreshKey => refreshKey + 1);
  };

  const onSortChange = sortObject => {
    if (!CommonUtility.isValidObject(sortObject)) return;
    const sortKey = Object.keys(sortObject)[0];
    const sortBy = sortObject[sortKey];

    const temp = {
      ...filter,
      page: 1,
      sort: {
        sortBy: sortKey,
        sortOrder: sortBy,
      },
    };
    setFilter({ ...temp });
  };

  const parsedSort = useMemo(
    () => ({
      [filter.sort.sortBy]: filter.sort.sortOrder,
    }),
    [filter],
  );

  return {
    data,
    loading,
    error,
    totalData,
    totalPages,
    filter,
    pageChanged,
    filterChanged,
    refresh,
    onSortChange,
    sort: parsedSort,
  };
};

export const palletLimboCheckHook = containerId => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        type: 'pallet',
        containerId,
      };
      const res = await ToteService.checkLimboState(params);
      setData(res?.data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (containerId) {
      fetchData();
    }
  }, [containerId]);

  return { data, loading, error };
};

export const PalletAutoCompleteHook = (warehouseId, search) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        warehouseId,
        sortBy: 'status',
        limit: 20,
        sortOrder: CommonConstant.sortDirection.ascending,
      } as any;

      if (search) {
        params.search = search.trim();
      }

      const res = await PalletService.get(params);
      setData(res?.data || []);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!warehouseId) {
      return;
    }

    fetchData();
  }, [search, warehouseId]);

  return {
    data,
    error,
    loading,
    totalData,
    totalPages,
    fetchData,
  };
};

type LabelHistoryResponse = {
  data?: PalletLabelDataType;
};

type PreviousPalletLabelHookReturnType = {
  loading: boolean;
  error: string | null;
  data?: PalletLabelDataType;
};

export const PreviousPalletLabelHook = (
  warehouseId: string,
  palletNumber: string,
): PreviousPalletLabelHookReturnType => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PalletLabelDataType>();
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        palletNumbers: palletNumber,
        warehouseId,
      };
      const res: LabelHistoryResponse = await PalletService.getPreviousLabel(params);
      setData(res?.data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (warehouseId && palletNumber) {
      fetchData();
    }
  }, [warehouseId, palletNumber]);

  return { data, loading, error };
};

const PALLET_FEED_QUERY_KEY = ['pallets', 'feed'] as const;

export type PalletFeedType = 'pallet-upload' | 'pallet-label-export';

export const PalletFeedHook = (
  warehouseId: string,
  type: PalletFeedType = 'pallet-upload',
) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({ limit: 10, page: 1 });
  const [debouncedFilter, setDebouncedFilter] = useState({ limit: 10, page: 1 });

  useDebouncedEffect(
    () => {
      if (filter.limit && filter.page) {
        setDebouncedFilter(filter);
      }
    },
    [filter],
    500,
  );

  const queryKey = [
    ...PALLET_FEED_QUERY_KEY,
    warehouseId,
    type,
    debouncedFilter,
    refreshKey,
  ];

  const {
    data: queryData,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await PalletService.getFeedLogs({
        ...debouncedFilter,
        warehouseId,
        type,
      });
      return {
        feeds: res?.data?.feeds ?? [],
        totalCount: res?.totalCount ?? 0,
        totalPages: res?.totalPages ?? 0,
      };
    },
    enabled: !!warehouseId,
  });

  const data = queryData?.feeds ?? [];
  const totalData = queryData?.totalCount ?? 0;
  const totalPages = queryData?.totalPages ?? 0;
  const error = queryError ?? null;

  useEffect(() => {
    setFilter(f => ({ ...f, page: 1 }));
  }, [warehouseId, type]);

  const pageChanged = (page: number) => {
    setFilter(f => ({ ...f, page }));
  };

  const filterChanged = (key: string, value: any) => {
    setFilter(f => ({ ...f, page: 1, [key]: value }));
  };

  const refresh = () => setRefreshKey(k => k + 1);

  return {
    data,
    error,
    filter,
    filterChanged,
    loading: isFetching,
    pageChanged,
    totalData,
    totalPages,
    refresh,
  };
};
